import React, { createContext, useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Provider che carica il piano di licenza + feature attive del tenant corrente.
 *
 * Espone:
 *  - tier: 'basic' | 'plus' | 'pro'
 *  - features: Set<string> — feature attive per il tier
 *  - hasFeature(name): boolean
 *  - refresh(): re-carica
 *  - PLAN_LABELS, PLAN_COLORS: mapping visivo
 */

const FeatCtx = createContext(null);

export const PLAN_LABELS = {
  basic: "BASE",
  plus: "PLUS",
  pro: "PRO",
};

export const PLAN_COLORS = {
  basic: { bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-300" },
  plus:  { bg: "bg-[#1E2D5B]", text: "text-white", border: "border-[#1E2D5B]" },
  pro:   { bg: "bg-gradient-to-br from-amber-400 to-amber-600", text: "text-[#1E2D5B]", border: "border-amber-500" },
};

// Descrizione umana per tooltip "Upgrade richiesto"
export const FEATURE_LABELS = {
  sdi_direct: "Invio SDI diretto",
  hotel_module: "Modulo Alberghiero",
  alloggiati_web: "Alloggiati Web (Polizia)",
  ross_1000: "ROSS 1000 (Istat)",
  ocr_documents: "Scansione documenti OCR",
  erp_export: "Export ERP contabile",
  multi_store: "Multi-sede",
  unlimited_operators: "Operatori illimitati",
  priority_support: "Supporto prioritario",
  multi_operator: "Multi-operatore (PIN)",
  operator_reports: "Report per operatore",
  top_customers: "Ranking top clienti",
  refunds: "Storni & resi",
  loyalty_qr: "QR fedeltà su scontrino",
  branding_advanced: "Personalizzazione avanzata",
  invoicing_manual: "Fatturazione elettronica",
  lottery_code: "Codice lotteria",
};

export function FeaturesProvider({ children }) {
  const { user, loading } = useAuth() || {};
  const [tier, setTier] = useState("basic");
  const [features, setFeatures] = useState([]);
  const [planId, setPlanId] = useState(null);
  const [activityType, setActivityType] = useState(null);
  const [tenantModes, setTenantModes] = useState([]);
  const [isDemo, setIsDemo] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState("trial");
  const [ready, setReady] = useState(false);

  const load = async () => {
    if (!user || user.role === "admin" || user.role === "superadmin") {
      setTier("pro");
      setFeatures([]);
      setTenantModes([]);
      setReady(true);
      return;
    }
    try {
      const r = await api.get("/tenant/features");
      setTier(r.data.tier || "basic");
      const feats = Array.isArray(r.data.features) ? r.data.features : [];
      setFeatures(feats);
      setPlanId(r.data.plan_id);
      setActivityType(r.data.activity_type || null);
      setTenantModes(Array.isArray(r.data.modes) ? r.data.modes : []);
      setIsDemo(!!r.data.is_demo);
      setSubscriptionStatus(r.data.subscription_status || "trial");
    } catch {
      setTier("basic");
      setFeatures([]);
      setTenantModes([]);
    } finally {
      setReady(true);
    }
  };

  useEffect(() => { if (!loading) load(); }, [loading, user?.id]);

  const hasFeature = (name) => {
    if (!user || !name) return false;
    if (user?.role === "superadmin" || user?.role === "admin") return true;
    const feats = Array.isArray(features) ? features : [];
    const modes = Array.isArray(tenantModes) ? tenantModes : [];
    const resto = modes.some((m) => ["ristorante", "bar", "pizzeria", "festa"].includes(m));
    if (name === "palmare") return resto && feats.includes("palmare");
    if (name === "hotel_module" || name === "alloggiati_web" || name === "ross_1000" || name === "ocr_documents") {
      return modes.includes("bnb") || modes.includes("alloggio_dedicato");
    }
    if (name === "gelateria") return modes.includes("gelateria");
    if (feats.includes(name)) return true;
    if (name === "appointments" && feats.includes("appuntamenti")) return true;
    if (name === "restaurant_reservations") {
      return feats.includes("restaurant_reservations") || modes.some((m) => ["ristorante", "pizzeria", "bar"].includes(m));
    }
    if (name === "stats" && (feats.includes("vendite") || feats.includes("report_essenziali") || feats.includes("daily_report"))) return true;
    if (name === "whatsapp_reminders" && (feats.includes("reminder_wa") || feats.includes("reminders_whatsapp") || feats.includes("reminder"))) return true;
    if (name === "warehouse" && (feats.includes("magazzino") || feats.includes("magazzino_avanzato"))) return true;
    if (name === "multi_store" && (feats.includes("multi_sede") || feats.includes("sedi") || feats.includes("multi_punto"))) return true;
    if (name === "multi_operator" && feats.includes("operatori")) return true;
    return false;
  };

  const requiredTier = (feature) => {
    // Determina il tier minimo che sblocca questa feature
    if (!feature) return "basic";
    // Mapping semplificato (potrebbe leggere da API /tenant/features)
    const PLUS = ["multi_operator", "operator_reports", "top_customers", "refunds", "loyalty_qr", "branding_advanced", "invoicing_manual", "lottery_code"];
    const PRO = ["sdi_direct", "hotel_module", "alloggiati_web", "ross_1000", "ocr_documents", "erp_export", "multi_store", "unlimited_operators", "priority_support"];
    if (PRO.includes(feature)) return "pro";
    if (PLUS.includes(feature)) return "plus";
    return "basic";
  };

  return (
    <FeatCtx.Provider value={{ tier, features, planId, subscriptionStatus, activityType, modes: tenantModes, isDemo, ready, hasFeature, requiredTier, refresh: load }}>
      {children}
    </FeatCtx.Provider>
  );
}

export const useFeatures = () => useContext(FeatCtx) || { tier: "basic", features: [], activityType: null, modes: [], isDemo: false, ready: false, hasFeature: () => false, requiredTier: () => "basic" };

const GateWait = () => (
  <div className="min-h-[40vh] flex items-center justify-center text-[#A1A1AA] text-sm">Caricamento…</div>
);

/** Blocca una pagina se la modalità cassa non è nel contratto del locale. */
export function RequireMode({ mode, any, children, fallback = "/dashboard" }) {
  const { modes, ready } = useFeatures();
  if (!ready) return <GateWait />;
  const list = Array.isArray(modes) ? modes : [];
  const ok = mode ? list.includes(mode) : (Array.isArray(any) ? any.some((m) => list.includes(m)) : false);
  if (!ok) return <Navigate to={fallback} replace />;
  return children;
}

/** Blocca una pagina se il modulo non è attivo sul contratto. */
export function RequireFeature({ name, children, fallback = "/dashboard" }) {
  const { hasFeature, ready } = useFeatures();
  if (!ready) return <GateWait />;
  if (name && !hasFeature(name)) return <Navigate to={fallback} replace />;
  return children;
}
